import React from "react";
import { strings } from "../../../resources/strings";
import {
  AnswerIcon,
  CHECK_PATH,
  CLOSE_PATH,
  answerButtonClass,
} from "./answerButtons";

export interface AnswerButtonRowProps {
  /** Affirmative-choice handler. */
  onYes: () => void;
  /** Negative-choice handler. */
  onNo: () => void;
  /** Whether the affirmative button reads as selected (aria-pressed + style). */
  yesSelected: boolean;
  /** Whether the negative button reads as selected. */
  noSelected: boolean;
  /** Affirmative label (default: localized "Yes"). */
  yesLabel?: string;
  /** Negative label (default: localized "No"). */
  noLabel?: string;
  /** Optional test id for the affirmative button. */
  yesTestId?: string;
  /** Optional test id for the negative button. */
  noTestId?: string;
}

/**
 * The Yes/No (affirmative/negative) root choice at the top of a workflow
 * question: two stacked answer buttons using the shared filled-green style and
 * check/close icons. Extracted from the four workflow forms (IHA-388), which
 * rendered identical markup differing only in labels, test ids, and handlers.
 *
 * (BoolQuestion intentionally keeps its own horizontal, outlined variant per
 * IHA-382, so it does not use this component.)
 */
const AnswerButtonRow: React.FC<AnswerButtonRowProps> = ({
  onYes,
  onNo,
  yesSelected,
  noSelected,
  yesLabel = strings.questionAnswers.yes,
  noLabel = strings.questionAnswers.no,
  yesTestId,
  noTestId,
}) => (
  <div className="flex flex-col gap-4">
    <button
      type="button"
      data-testid={yesTestId}
      onClick={onYes}
      aria-pressed={yesSelected}
      className={answerButtonClass(yesSelected)}
    >
      <AnswerIcon testId="answer-icon-yes" path={CHECK_PATH} />
      {yesLabel}
    </button>
    <button
      type="button"
      data-testid={noTestId}
      onClick={onNo}
      aria-pressed={noSelected}
      className={answerButtonClass(noSelected)}
    >
      <AnswerIcon testId="answer-icon-no" path={CLOSE_PATH} />
      {noLabel}
    </button>
  </div>
);

export default AnswerButtonRow;
